import React, { FC, useEffect, useState } from 'react'

import classNames from 'classnames'

import ResultsCount from '@components/music/ResultsCount'
import FacetFilter from '@components/music/filter/FacetFilter'
import useOutsideClick from '@hooks/useOutsideClick'
import { usePageApi } from '@providers/PageApiProvider'
import { SearchPageApiResponse } from '@providers/types/api-responses'

import { mapAggregationsToFacets } from './utils/helpers'
import { Facet } from './utils/types'

interface Props {
  visible: boolean
  setSideFilterVisibility: (visible: boolean) => void
}

const Filter: FC<Props> = ({ visible, setSideFilterVisibility }: Props) => {
  const { pageData, loading } = usePageApi<SearchPageApiResponse>()
  const [facets, setFacets] = useState<Facet[]>([])
  const clickHandler = (): void => setSideFilterVisibility(!visible)

  useEffect(() => {
    if (pageData?.aggregations && !loading) {
      const facets = mapAggregationsToFacets(pageData.aggregations)
      setFacets(facets)
    }
  }, [pageData, loading])

  // If Filter component is open and the user clicks outside of it close the panel
  const filterContainerRef = useOutsideClick<HTMLDivElement>(
    () => visible && setSideFilterVisibility(false)
  )

  return (
    <div
      data-testid="music-filter"
      className={classNames(
        'absolute md:relative w-[18rem] min-w-[18rem] h-full overflow-y-auto transition-margin duration-300 ease-in-out md:!ml-0 z-20',
        visible ? 'ml-0' : 'ml-[-310px]'
      )}>
      <div
        className={classNames(
          'fixed inset-0 block md:hidden bg-black bg-opacity-20',
          visible ? 'backdrop-blur-[1px] z-[1]' : 'backdrop-blur-none z-[-1]',
          {
            hidden: !visible,
          }
        )}
      />
      <div className="hidden justify-start items-center py-8 mx-5 px-3 text-base border-b md:flex text-gray-text border-gray dark:border-gray-border-dm">
        <ResultsCount />
      </div>
      <div
        ref={filterContainerRef}
        className={classNames(
          'fixed top-0 md:static z-20 bg-gray-light dark:bg-black-bg h-screen md:h-auto shadow-md shadow-black md:shadow-none blur-none overflow-y-auto min-w-[300px] md:min-w-0'
        )}
        data-testid="filter">
        <div className="sticky top-0">
          <div className="flex justify-between pt-5 pl-2.5 mx-4 pb-4 pr-1 border-b md:hidden border-gray dark:border-gray-border-dm">
            <div className="text-base font-thin text-gray-text">
              <ResultsCount />
            </div>
            <button
              type="button"
              onClick={clickHandler}
              className="cursor-pointer hover:opacity-50">
              <i className="nf-icon nf-close text-xl text-gray-icons" />
            </button>
          </div>
          {facets.map((facet) => (
            <FacetFilter key={facet.category} facet={facet} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default Filter
